import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Card, ListGroup, Badge, Alert, Spinner } from "react-bootstrap";
import { FaEdit, FaSearch, FaCheck, FaSave, FaTimes, FaCamera, FaUsers, FaExclamationTriangle } from "react-icons/fa";

const ClassroomEditModal = ({ show, onHide, schoolid, getClassesBySchoolId, updateClassName }) => {
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [editedClassName, setEditedClassName] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Filter states
  const [currentGradeFilter, setCurrentGradeFilter] = useState("all");
  const [academicYearFilter, setAcademicYearFilter] = useState("all");
  const [snapshotFilter, setSnapshotFilter] = useState("all");
  const [classSearchTerm, setClassSearchTerm] = useState("");

  // Available grade levels
  const gradeLevels = [
    { value: "all", label: "All Grades" },
    { value: "grade_level1", label: "Junior Infants" },
    { value: "grade_level2", label: "Senior Infants" },
    { value: "grade_level3", label: "1st Class" },
    { value: "grade_level4", label: "2nd Class" },
    { value: "grade_level5", label: "3rd Class" },
    { value: "grade_level6", label: "4th Class" },
    { value: "grade_level7", label: "5th Class" },
    { value: "grade_level8", label: "6th Class" }
  ];

  // Academic years - will be populated from actual data
  const [availableAcademicYears, setAvailableAcademicYears] = useState([
    { value: "all", label: "All Academic Years" }
  ]);

  // Snapshot filter options
  const snapshotOptions = [
    { value: "all", label: "All Classes" },
    { value: "active", label: "Active Classes Only" },
    { value: "snapshot", label: "Snapshots Only" }
  ];

  const getGradeLabelFromClass = (classItem) => {
    if (classItem.grade_level1) return "Junior Infants";
    if (classItem.grade_level2) return "Senior Infants";
    if (classItem.grade_level3) return "1st Class";
    if (classItem.grade_level4) return "2nd Class";
    if (classItem.grade_level5) return "3rd Class";
    if (classItem.grade_level6) return "4th Class";
    if (classItem.grade_level7) return "5th Class";
    if (classItem.grade_level8) return "6th Class";
    return "Unknown Grade";
  };

  const getGradeBooleanFromClass = (classItem) => {
    if (classItem.grade_level1) return "grade_level1";
    if (classItem.grade_level2) return "grade_level2";
    if (classItem.grade_level3) return "grade_level3";
    if (classItem.grade_level4) return "grade_level4";
    if (classItem.grade_level5) return "grade_level5";
    if (classItem.grade_level6) return "grade_level6";
    if (classItem.grade_level7) return "grade_level7";
    if (classItem.grade_level8) return "grade_level8";
    return "unknown";
  };

  useEffect(() => {
    if (show && schoolid) {
      loadData();
    }
  }, [show, schoolid]);

  useEffect(() => {
    // Check for unsaved changes
    if (selectedClass && editedClassName !== selectedClass.name) {
      setHasUnsavedChanges(true);
    } else {
      setHasUnsavedChanges(false);
    }
  }, [editedClassName, selectedClass]);

  const loadData = async () => {
    setLoading(true);
    setError("");
    try {
      const classesResponse = await getClassesBySchoolId(schoolid);

      // Transform class data
      const transformedClasses = (classesResponse || []).map(classItem => {
        const gradeLabel = getGradeLabelFromClass(classItem);
        const gradeBoolean = getGradeBooleanFromClass(classItem);
        
        return {
          id: classItem.id,
          name: classItem.name,
          grade: gradeLabel,
          gradeBoolean: gradeBoolean,
          academicYear: classItem.academicYear || null, // Use direct field from API
          snapshot: classItem.snapshot || false, // Add snapshot field
          studentNumber: classItem.studentNumber || 0, // Add student count
          teacher: classItem.teacher?.name || classItem.teacherName || '',
          teacherId: classItem.teacherId,
          active: classItem.active,
          trial_class: classItem.trial_class,
          date_updated: classItem.date_updated,
          // Keep original data
          ...classItem
        };
      }).sort((a, b) => {
        // Sort by academic year (newest first), then by name
        if (a.academicYear !== b.academicYear) {
          return b.academicYear?.localeCompare(a.academicYear || '') || 0;
        }
        return a.name.localeCompare(b.name);
      });

      // Extract unique academic years for filter options
      const uniqueAcademicYears = [...new Set(transformedClasses
        .map(cls => cls.academicYear)
        .filter(year => year) // Remove null/undefined
      )].sort().reverse(); // Sort newest first

      const academicYearOptions = [
        { value: "all", label: "All Academic Years" },
        ...uniqueAcademicYears.map(year => ({
          value: year,
          label: `${year} Academic Year`
        }))
      ];

      setAvailableAcademicYears(academicYearOptions);
      setClasses(transformedClasses);

    } catch (err) {
      setError("Failed to load classes. Please try again.");
      console.error("Error loading classes:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleClassSelect = (classItem) => {
    // Check for unsaved changes before switching
    if (hasUnsavedChanges) {
      const confirmSwitch = window.confirm("You have unsaved changes. Are you sure you want to switch classes? Your changes will be lost.");
      if (!confirmSwitch) {
        return;
      }
    }

    setSelectedClass(classItem);
    setEditedClassName(classItem.name);
    setError("");
    setSuccess("");
    setHasUnsavedChanges(false);
  };

  const handleSaveChanges = async () => {
    if (!selectedClass || !editedClassName.trim()) {
      setError("Please enter a valid class name.");
      return;
    }

    if (editedClassName.trim() === selectedClass.name) {
      setError("No changes detected.");
      return;
    }

    setSaving(true);
    setError("");
    setSuccess("");

    try {
      // Call your API to update the class name
      // Assuming updateClassName takes (classId, newName) parameters
      await updateClassName(selectedClass.id, editedClassName.trim());

      setSuccess(`Class name successfully updated to "${editedClassName.trim()}"`);
      
      // Update the local state
      setClasses(prev => prev.map(classItem => 
        classItem.id === selectedClass.id 
          ? { ...classItem, name: editedClassName.trim() }
          : classItem
      ));

      // Update selected class
      setSelectedClass(prev => ({ ...prev, name: editedClassName.trim() }));
      setHasUnsavedChanges(false);

    } catch (err) {
      setError(`Failed to update class name: ${err.message}`);
      console.error("Error updating class name:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleDiscardChanges = () => {
    if (selectedClass) {
      setEditedClassName(selectedClass.name);
      setHasUnsavedChanges(false);
      setError("");
      setSuccess("");
    }
  };

  const getFilteredClasses = () => {
    return classes.filter(classItem => {
      const matchesGrade = currentGradeFilter === "all" || classItem.gradeBoolean === currentGradeFilter;
      const matchesAcademicYear = academicYearFilter === "all" || classItem.academicYear === academicYearFilter;
      
      // Snapshot filtering logic
      let matchesSnapshot = true;
      if (snapshotFilter === "active") {
        matchesSnapshot = !classItem.snapshot;
      } else if (snapshotFilter === "snapshot") {
        matchesSnapshot = classItem.snapshot;
      }
      
      const matchesSearch = classItem.name?.toLowerCase().includes(classSearchTerm.toLowerCase()) ||
                           (classItem.teacher && classItem.teacher.toLowerCase().includes(classSearchTerm.toLowerCase()));
      return matchesGrade && matchesAcademicYear && matchesSnapshot && matchesSearch;
    });
  };

  const handleClose = () => {
    if (hasUnsavedChanges) {
      const confirmClose = window.confirm("You have unsaved changes. Are you sure you want to close? Your changes will be lost.");
      if (!confirmClose) {
        return;
      }
    }

    setSelectedClass(null);
    setEditedClassName("");
    setError("");
    setSuccess("");
    setCurrentGradeFilter("all");
    setAcademicYearFilter("all");
    setSnapshotFilter("all");
    setClassSearchTerm("");
    setHasUnsavedChanges(false);
    onHide();
  };

  const filteredClasses = getFilteredClasses();

  return (
    <Modal show={show} onHide={handleClose} size="xl" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          <FaEdit className="me-2" />
          Classroom Edit
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body style={{ minHeight: '600px' }}>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        <Row className="h-100">
          {/* Left Panel - Classes */}
          <Col md={6} className="border-end">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">
                Select Class to Edit
                <Badge bg="secondary" className="ms-2">{filteredClasses.length} classes</Badge>
              </h5>
              {selectedClass && (
                <Badge bg="primary">
                  Selected: {selectedClass.name}
                </Badge>
              )}
            </div>

            {/* Class Filters */}
            <Row className="mb-3">
              <Col md={3}>
                <Form.Select 
                  value={academicYearFilter} 
                  onChange={(e) => setAcademicYearFilter(e.target.value)}
                  size="sm"
                >
                  {availableAcademicYears.map(year => (
                    <option key={year.value} value={year.value}>
                      {year.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Select 
                  value={currentGradeFilter} 
                  onChange={(e) => setCurrentGradeFilter(e.target.value)}
                  size="sm"
                >
                  {gradeLevels.map(grade => (
                    <option key={grade.value} value={grade.value}>
                      {grade.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Select 
                  value={snapshotFilter} 
                  onChange={(e) => setSnapshotFilter(e.target.value)}
                  size="sm"
                >
                  {snapshotOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3}>
                <div className="position-relative">
                  <Form.Control
                    type="text"
                    placeholder="Search..."
                    value={classSearchTerm}
                    onChange={(e) => setClassSearchTerm(e.target.value)}
                    size="sm"
                  />
                  <FaSearch className="position-absolute top-50 end-0 translate-middle-y me-2 text-muted" />
                </div>
              </Col>
            </Row>

            {/* Classes List */}
            <div style={{ height: '450px', overflowY: 'auto' }}>
              {loading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Loading classes...
                </div>
              ) : filteredClasses.length === 0 ? (
                <div className="text-center py-4 text-muted">No classes found</div>
              ) : (
                <div className="d-grid gap-2">
                  {filteredClasses.map(classItem => (
                    <Card
                      key={classItem.id}
                      className={`cursor-pointer ${
                        selectedClass?.id === classItem.id ? 'border-primary bg-light' : ''
                      }`}
                      onClick={() => handleClassSelect(classItem)}
                      style={{ cursor: 'pointer' }}
                    >
                      <Card.Body className="py-3">
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="flex-grow-1">
                            <div className="d-flex align-items-center mb-2">
                              <div className="fw-bold me-2">{classItem.name}</div>
                              {classItem.snapshot && (
                                <Badge bg="secondary" className="me-2">
                                  <FaCamera className="me-1" size={10} />
                                  Snapshot
                                </Badge>
                              )}
                              {classItem.trial_class && (
                                <Badge bg="warning" text="dark" className="me-2">Trial</Badge>
                              )}
                              <Badge 
                                bg={classItem.trial_class ? "warning" : "secondary"} 
                                text={classItem.trial_class ? "dark" : "white"}
                              >
                                {classItem.grade}
                              </Badge>
                            </div>
                            <div className="small text-muted">
                              {classItem.academicYear && (
                                <span className="me-3">{classItem.academicYear}</span>
                              )}
                              {!classItem.snapshot && classItem.studentNumber > 0 && (
                                <Badge bg="light" text="dark">
                                  <FaUsers className="me-1" size={10} />
                                  {classItem.studentNumber} student{classItem.studentNumber !== 1 ? 's' : ''}
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div className="text-end">
                            {selectedClass?.id === classItem.id && (
                              <FaCheck className="text-primary" size={20} />
                            )}
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </Col>

          {/* Right Panel - Class Details */}
          <Col md={6}>
            {!selectedClass ? (
              <div className="text-center py-5">
                <FaEdit className="text-muted mb-3" size={48} />
                <h5 className="text-muted">Select a class to edit</h5>
                <p className="text-muted">Choose a class from the list to view and edit its details.</p>
              </div>
            ) : (
              <div>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h5 className="mb-0 d-flex align-items-center">
                    Edit Class Details
                    {selectedClass.snapshot && (
                      <Badge bg="secondary" className="ms-2">
                        <FaCamera className="me-1" size={10} />
                        Snapshot
                      </Badge>
                    )}
                  </h5>
                  {hasUnsavedChanges && (
                    <Badge bg="warning" text="dark">
                      Unsaved Changes
                    </Badge>
                  )}
                </div>

                {/* Snapshot Warning */}
                {selectedClass.snapshot && (
                  <Alert variant="warning" className="mb-4">
                    <FaExclamationTriangle className="me-2" />
                    <strong>Snapshot Class Warning:</strong> You are editing a snapshot class. 
                    Snapshot classes contain historical data. Changes should be made carefully.
                  </Alert>
                )}

                {/* Class Info Summary */}
                <Card className="mb-4 bg-light">
                  <Card.Body>
                    <Row>
                      <Col md={6}>
                        <div className="mb-2">
                          <small className="text-muted">Grade Level</small>
                          <div className="fw-medium">{selectedClass.grade}</div>
                        </div>
                        <div className="mb-2">
                          <small className="text-muted">Academic Year</small>
                          <div className="fw-medium">{selectedClass.academicYear || 'Not specified'}</div>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-2">
                          <small className="text-muted">Status</small>
                          <div>
                            <Badge bg={selectedClass.trial_class ? "warning" : selectedClass.active ? "success" : "secondary"}>
                              {selectedClass.trial_class ? "Trial Class" : selectedClass.active ? "Active" : "Inactive"}
                            </Badge>
                          </div>
                        </div>
                        {!selectedClass.snapshot && selectedClass.studentNumber > 0 && (
                          <div className="mb-2">
                            <small className="text-muted">Enrollment</small>
                            <div>
                              <Badge bg="light" text="dark">
                                <FaUsers className="me-1" size={10} />
                                {selectedClass.studentNumber} student{selectedClass.studentNumber !== 1 ? 's' : ''}
                              </Badge>
                            </div>
                          </div>
                        )}
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                {/* Editable Fields */}
                <div className="mb-4">
                  <h6 className="mb-3">Editable Fields</h6>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Class Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={editedClassName}
                      onChange={(e) => setEditedClassName(e.target.value)}
                      placeholder="Enter class name"
                      maxLength={100}
                    />
                    <Form.Text className="text-muted">
                      {editedClassName.length}/100 characters
                    </Form.Text>
                  </Form.Group>

                  {/* Action Buttons */}
                  <div className="d-flex gap-2">
                    <Button 
                      variant="success" 
                      onClick={handleSaveChanges}
                      disabled={!hasUnsavedChanges || saving || !editedClassName.trim()}
                    >
                      {saving ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <FaSave className="me-2" />
                          Save Changes
                        </>
                      )}
                    </Button>
                    
                    <Button 
                      variant="outline-secondary" 
                      onClick={handleDiscardChanges}
                      disabled={!hasUnsavedChanges || saving}
                    >
                      <FaTimes className="me-2" />
                      Discard Changes
                    </Button>
                  </div>

                  {/* Save Progress Indicator */}
                  {hasUnsavedChanges && (
                    <div className="mt-3">
                      <small className="text-warning">
                        <FaExclamationTriangle className="me-1" />
                        You have unsaved changes. Don't forget to save!
                      </small>
                    </div>
                  )}
                </div>
              </div>
            )}
          </Col>
        </Row>
      </Modal.Body>

      <Modal.Footer>
        <div className="d-flex justify-content-between w-100 align-items-center">
          <div className="text-muted">
            {selectedClass && (
              <span>
                Editing: <strong>{selectedClass.name}</strong>
                {selectedClass.snapshot && (
                  <Badge bg="secondary" className="ms-2">
                    <FaCamera className="me-1" size={10} />
                    Snapshot
                  </Badge>
                )}
                {hasUnsavedChanges && (
                  <span className="text-warning ms-2">
                    <FaExclamationTriangle className="me-1" />
                    Unsaved changes
                  </span>
                )}
              </span>
            )}
          </div>
          <Button variant="secondary" onClick={handleClose}>
            {hasUnsavedChanges ? "Close (Discard Changes)" : "Close"}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default ClassroomEditModal;